// QR Kiosk v2 — KV2-9 plate spec row (the designer's `NPRow` atom, kiosk-directions.jsx
// lines 261-271, ported to tokens). Label left, mono value right-aligned, optional mono
// sub-line beneath. Used 8× by PlateCard.
import React from "react";

export interface PlateRowProps {
  label: string;
  value: React.ReactNode;
  /** Optional mono sub-line (only consumer today: typeName under the product designation). */
  sub?: string | null;
  /** Hairline above the row — false for the first row of each block. */
  top?: boolean;
}

export function PlateRow({ label, value, sub, top = true }: PlateRowProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 12,
        padding: "10px 16px",
        borderTop: top ? "1px solid var(--border-soft)" : "none",
      }}
    >
      {/* The label lane is a FIXED 118px column and must hold one line. The shared .eyebrow
          class is deliberately NOT used here: its kiosk-legibility size (11.5px) overflows
          118px on the longer localized labels ("OZNAČENÍ STROJE"), wrapping to two lines and
          doubling the row height — that was the whole of the density drift. 10px mono at
          0.1em is the artboard value and fits every locale in the lane.

          WEIGHT IS EXPLICIT AND MUST STAY THAT WAY. Dropping .eyebrow also dropped its
          `font-weight: 600`, and this span had no weight of its own, so the entire label
          column silently fell to the .qr-kiosk-v2-root inherited 500 while shrinking
          11.5px → 10px — small AND light, which is what the owner read as "thin font"
          (2026-08-13: "the paper design has the font in BOLD which is better readable.
          Don't use thin font"). The artboard draws the label at 400; the owner's later
          instruction overrides the canvas here, exactly as the kiosk-legibility
          recalibration already overrides MIDAS-exact values elsewhere in this sheet.
          600 also matches the slate band eyebrow directly above it. */}
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          lineHeight: "15px",
          color: "var(--muted)",
          width: 118,
          // L6: one unbreakable localized word wider than the lane (DE PRODUKTBEZEICHNUNG =
          // 126px) widens THIS row's lane instead of spilling into the 12px gap. Multi-word
          // labels still wrap at 118; values are right-aligned so lane width never shows.
          minWidth: "min-content",
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <span style={{ flex: 1, minWidth: 0, textAlign: "right" }}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 13.5,
            lineHeight: "18px",
            fontWeight: 600,
            color: "var(--ink)",
            overflowWrap: "anywhere",
          }}
        >
          {value}
        </span>
        {sub && (
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              lineHeight: "12px",
              color: "var(--faint)",
              marginTop: 2,
            }}
          >
            {sub}
          </div>
        )}
      </span>
    </div>
  );
}

export default PlateRow;
